/**
 * 券墙场景
 */
export enum CouponWallScene {
  FX_CENTER,
  WAIMAI,
}

/**
 * 券样式配置类型
 */
export interface CouponStyleConfig {
  showPrice: boolean; // 是否展示券金额
  countdownAnimationToggle: boolean; // 自动领取开关
}

/**
 * 券墙Tab类型
 */
export interface CouponWallTab {
  tabIcon: string; // 券墙TabID
  tabName: string; // 券墙Tab标题
  planCode: string; // 权益池编码（planCodeList 的第一个值）
  planCodeList?: string[]; // Tab 内所有券的 planCode 列表，未改造的页面不下发此字段
}

/**
 * 节日券配置类型
 */
export interface FestivalCouponConfig {
  enable: boolean; // 节日券功能开关
  festivalCouponList: string[] | null; // 节日券ID列表
}
/**
 * 券类型
 */
export enum CouponType {
  NORMAL = 1, // 券
  EXPAND = 2, // 膨胀券
}

/**
 * 跳转类型枚举
 */
export enum JUMP_LINK_TYPE {
  H5 = 1, // H5链接
  MT_MINI = 3, // 外卖小程序
  MT_APP = 4, // 美团APP
}

/**
 * 券操作类型
 */
export interface CouponOperation {
  jumpLink: string; // 跳转链接
  jumpLinkType: JUMP_LINK_TYPE; // 跳转类型
  appId?: string; // 小程序ID
}

/**
 * 券供给类型
 */
export interface CouponSupply {
  supplyType: number; // 供给类型: 1-韦恩供给
  wayneActId: number; // 玩法ID
  recallable?: boolean | null; // 是否可召回
  segmentIdList: number[]; // recallable 为 true 时，会下发券对应的数据片 ID 列表
}

export enum CouponGrantSource {
  NEW = 1, // 本次领取
  OLD = 2, // 历史已领
}

/**
 * 券单项
 */
export interface CouponData {
  actSysSrc: string; // 活动系统来源
  planCode: string; // 权益池编码
  rightCode: string; // 权益编码
  couponValue: number; // 券金额
  priceLimit: number; // 使用门槛
  couponName: string; // 券名称
  couponEndTime: number; // 券结束时间
  couponConfigId: string; // 券批次ID
  operation: CouponOperation | null; // 券操作
  couponViewId: string | null; // 券ID
  couponSupply: CouponSupply | null; // 券供应方
  isFestivalCoupon?: boolean; // 是否为节日券
  grantSource?: CouponGrantSource; // 券来源
}

/** 金额格式化后的券数据 */
export interface EnhancedCouponData extends Omit<CouponData, "couponValue" | "priceLimit"> {
  couponValue: string;
  priceLimit: string;
}

/**
 * 券墙列表单项类型
 */
export interface CouponWallItem {
  type: CouponType; // 券类型
  data: CouponData;
}

/**
 * 标题项
 */
export interface TitleItem {
  text: string;
  color: string;
}

export interface CouponActivityConfig {
  activityName: string; // 活动名称
  activityCode: string; // 活动编码
  activityScene: number; // 活动场景
  couponStyleConfig: CouponStyleConfig;
  tabList: CouponWallTab[];
  festivalCouponConfig: FestivalCouponConfig;
}

export interface ActivityCouponResult {
  currentTime: number; // 当前时间戳
  config: CouponActivityConfig; // 配置项
  list: CouponWallItem[];
  title: TitleItem[];
  recallToken: string; // 召回token
  quickScreenCouponList: CouponData[];
}

// 发放结果
export interface GrantRightsTab {
  planCode: string;
  rightCodes: string[];
}

export interface ReceiveCouponTab {
  planCode: string;
  couponList: CouponData[];
}

// 领券接口返回数据
export interface ReceiveCouponData {
  currentTime: number; // 当前时间戳
  title: TitleItem[];
  tabs: ReceiveCouponTab[];
  quickScreenCouponList: CouponData[];
}

// 领券接口回传预发券的券列表，只回传精简的数据
export type GrantCouponItem = Pick<CouponData, 'couponValue' | 'couponViewId' | 'couponConfigId' | 'planCode' | 'rightCode'>;

// 韦恩供给召回判定返回结果
export interface WayneSupplyRecallResult {
  recallableWayneActIdList: number[]; // 可召回的韦恩ID列表
  recallableWayneList: CouponSupply[]; // wayneActId 对应的 couponSupply 信息
}

export interface CouponWallSingleStyle {
  couponWallBg: string; // 券墙背景
  couponNameColor: string;
  couponBtnBg: string;
  couponBtnTextColor: string;
  couponExpireColor: string;
  festivalCouponWallBg: string;
  festivalCouponNameColor: string;
  festivalCouponBtnBg: string;
  festivalCouponBtnTextColor: string;
  festivalCouponExpireColor: string;
}

/**
 * 券墙静态图相关状态枚举
 */
export enum PreviewStatus {
  LOADING = 'loading',
  RECEIVING = 'receiving',
  NOT_LOGIN = 'notLogin',
}

/**
 * 券墙组件Props
 */
export interface CouponWallProps {
  scene: CouponWallScene; // 券墙场景
  // 券墙相关样式配置
  couponWallStyle?: {
    couponWallBottomBgUrl?: string; // 券墙底部图片
    couponWallPreviewImageConfig?: Record<PreviewStatus, string>; // 券墙静态占位图配置
  };
  // 是否开启少量券样式适配
  enableFewCouponStyleFit?: boolean;
  // 灵犀埋点配置
  pageCid: string; // 页面CID
  isHitCouponOptimizeExp?: boolean; // 是否命中券墙优化实验
}

/**
 * 券状态
 */
export enum CouponStatus {
  NOT_RECEIVED = 'not_received', // 未领取
  RECEIVED = 'received', // 已领取，无可使用按钮
  CAN_USE = 'can_use', // 可使用，有可使用按钮
  EXPIRED = 'expired', // 已过期
}

/**
 * 发券身份
 */
export enum SendCouponIdentityScene {
  DISTRIBUTION_VENUE = 4, // 分销中心页
}

/**
 * 发券场景，红包墙不传
 */
export enum SendCouponScene {
  DISTRIBUTION_VENUE = 13, // 限时抢
}

/**
 * 身份场景信息
 */
export interface IdentityScene {
  sendCouponIdentity: SendCouponIdentityScene; // 发券身份
  sendCouponScene: SendCouponScene | null; // 发券场景
}
